import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@soroush/ui';
import { apiFetch } from '../lib/api';
import { formatFlightTime, formatMoney } from '../lib/format';
import { BookingSteps } from '../components/BookingSteps';
import { FlightSummaryCard } from '../components/FlightSummaryCard';
import { readRoundTripHolds } from '../lib/roundTrip';

interface BookingDetails {
  _id: string;
  pnr: string;
  status: string;
  contactEmail: string;
  cabin: string;
  currency: string;
  totalAmount: number;
  holdExpiresAt?: string;
  ticketNumbers: string[];
  passengers: Array<{
    firstName: string;
    lastName: string;
    documentNumber: string;
    ticketNumber?: string;
  }>;
  nrs?: { pnr: string; ticketingDelayed?: boolean };
  metadata?: { roundTrip?: { role?: 'outbound' | 'return'; companionId?: string } };
  flightId: {
    flightNumber: string;
    origin: string;
    destination: string;
    departureAt: string;
  };
}

const TICKET_STEP = 4;
const PAYMENT_STEP = 3;
const POLL_MS = 4000;
const MAX_POLLS = 30;

function readPendingFlights() {
  try {
    const raw = sessionStorage.getItem('sa_pending_flights');
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function BookingConfirmationPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isFa = i18n.language.startsWith('fa');
  const [params] = useSearchParams();
  const bookingId = params.get('bookingId');
  const refundFlag = params.get('payment') === 'refund';
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [companion, setCompanion] = useState<BookingDetails | null>(null);
  const [error, setError] = useState('');
  const [retrying, setRetrying] = useState(false);
  const [polls, setPolls] = useState(0);
  const [pendingFlights, setPendingFlights] = useState<Array<Record<string, unknown>>>([]);
  const holds = readRoundTripHolds();
  const sessionCompanion =
    holds && bookingId
      ? holds.outboundId === bookingId
        ? holds.returnId
        : holds.returnId === bookingId
          ? holds.outboundId
          : ''
      : '';
  const linkedCompanion = booking?.metadata?.roundTrip?.companionId ?? '';
  const companionId = linkedCompanion || sessionCompanion;

  useEffect(() => {
    if (!bookingId) {
      setError(t('common.errorGeneric'));
      return;
    }
    let cancelled = false;
    apiFetch<{ booking: BookingDetails }>(`/api/v1/bookings/${encodeURIComponent(bookingId)}`)
      .then((data) => {
        if (!cancelled) setBooking(data.booking);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || t('common.errorGeneric'));
      });
    return () => {
      cancelled = true;
    };
  }, [bookingId, t, polls]);

  useEffect(() => {
    if (!companionId) return;
    let cancelled = false;
    apiFetch<{ booking: BookingDetails }>(`/api/v1/bookings/${encodeURIComponent(companionId)}`)
      .then((data) => {
        if (!cancelled) setCompanion(data.booking);
      })
      .catch(() => {
        if (!cancelled) setCompanion(null);
      });
    return () => {
      cancelled = true;
    };
  }, [companionId, polls]);

  async function payBooking(id: string) {
    setRetrying(true);
    setError('');
    try {
      const pay = await apiFetch<{ redirectUrl: string }>('/api/v1/payments/initiate', {
        method: 'POST',
        body: JSON.stringify({ bookingId: id }),
      });
      window.location.href = pay.redirectUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.errorGeneric'));
      setRetrying(false);
    }
  }

  const confirmed = booking?.status === 'CONFIRMED';
  useEffect(() => {
    if (confirmed) setPendingFlights(readPendingFlights());
  }, [confirmed]);
  const companionTicketing = companion?.status === 'TICKETING';
  const ticketing = booking?.status === 'TICKETING' || companionTicketing;
  const legRole = (item: BookingDetails | null) => {
    if (!item) return null;
    const role = item.metadata?.roundTrip?.role;
    if (role === 'outbound' || role === 'return') return role;
    if (holds?.outboundId === item._id) return 'outbound' as const;
    if (holds?.returnId === item._id) return 'return' as const;
    return null;
  };
  const outboundBooking = legRole(booking) === 'outbound' ? booking : legRole(companion) === 'outbound' ? companion : null;
  const returnBooking = legRole(booking) === 'return' ? booking : legRole(companion) === 'return' ? companion : null;
  const outboundReady = Boolean(
    outboundBooking &&
      (outboundBooking.status === 'CONFIRMED' || outboundBooking.status === 'TICKETING'),
  );
  const returnHold =
    outboundReady &&
    returnBooking &&
    returnBooking.status === 'HOLD' &&
    (!returnBooking.holdExpiresAt || new Date(returnBooking.holdExpiresAt) > new Date())
      ? returnBooking
      : null;
  useEffect(() => {
    if (!ticketing || polls >= MAX_POLLS) return;
    const timer = window.setTimeout(() => setPolls((n) => n + 1), POLL_MS);
    return () => window.clearTimeout(timer);
  }, [ticketing, polls]);
  const holdActive =
    booking?.status === 'HOLD' &&
    (!booking.holdExpiresAt || new Date(booking.holdExpiresAt) > new Date());
  const cancelledAfterPayment =
    refundFlag || (booking?.status === 'CANCELLED' && Boolean(booking.nrs) && !holdActive);
  const ticketingDelayed = ticketing && (polls >= MAX_POLLS || booking?.nrs?.ticketingDelayed);
  const reference = booking?.nrs?.pnr ?? booking?.pnr;

  let statusMessage = null;
  if (booking && !confirmed) {
    if (ticketing) {
      statusMessage = (
        <div className="sa-alert" role="status">
          <strong>{t('common.ticketing')}</strong>
          <p className="sa-text-muted" style={{ marginTop: '0.5rem' }}>
            {ticketingDelayed
              ? t('common.ticketingDelayed', { email: booking.contactEmail })
              : t('common.ticketingLead')}
          </p>
        </div>
      );
    } else if (cancelledAfterPayment) {
      statusMessage = (
        <div className="sa-alert sa-alert--error" role="alert">
          {t('common.refundNotice')}
        </div>
      );
    } else if (booking.status === 'CANCELLED') {
      statusMessage = (
        <div className="sa-alert sa-alert--error" role="alert">
          {t('common.bookingCancelled')}
        </div>
      );
    } else {
      statusMessage = (
        <div className="sa-alert sa-alert--error" role="alert">
          {holdActive && booking.holdExpiresAt
            ? t('common.paymentFailed', {
                time: formatFlightTime(booking.holdExpiresAt, i18n.language),
              })
            : t('common.paymentFailedExpired')}
        </div>
      );
    }
  }

  return (
    <main className="sa-page">
      <div className="sa-container">
        <BookingSteps
          current={
            returnHold ? PAYMENT_STEP : confirmed ? TICKET_STEP + 1 : ticketing ? TICKET_STEP : PAYMENT_STEP
          }
        />

        {error ? (
          <div className="sa-alert sa-alert--error" role="alert">
            {error}
          </div>
        ) : null}
        {!booking && !error ? <p className="sa-text-muted">{t('common.loading')}</p> : null}

        {booking ? (
          <div className="booking-layout">
            <div className="booking-layout__main">
              {confirmed ? (
                <section className="booking-panel booking-success" aria-labelledby="ticket-title">
                  <span className="booking-success__icon" aria-hidden="true">
                    <svg viewBox="0 0 20 20" width="28" height="28" focusable="false">
                      <path
                        fill="currentColor"
                        d="M8.1 13.7 4.6 10.2l1.3-1.3 2.2 2.2 5.9-5.9 1.3 1.3-7.2 7.2z"
                      />
                    </svg>
                  </span>
                  <h1 id="ticket-title" className="booking-success__title">
                    {returnHold ? t('common.outboundTicketIssued') : t('common.ticketIssued')}
                  </h1>
                  <p className="sa-text-muted">
                    {t('common.ticketIssuedLead', { email: booking.contactEmail })}
                  </p>
                  <p className="booking-success__pnr">
                    <span>
                      {legRole(booking)
                        ? t(
                            legRole(booking) === 'outbound'
                              ? 'common.outboundFlight'
                              : 'common.returnFlight',
                          )
                        : booking.nrs
                          ? t('common.airlineReference')
                          : t('common.pnr')}
                    </span>
                    <strong dir="ltr">{reference}</strong>
                  </p>
                  {companion?.status === 'CONFIRMED' ? (
                    <p className="booking-success__pnr">
                      <span>
                        {t(
                          legRole(companion) === 'outbound'
                            ? 'common.outboundFlight'
                            : 'common.returnFlight',
                        )}
                      </span>
                      <strong dir="ltr">{companion.nrs?.pnr ?? companion.pnr}</strong>
                    </p>
                  ) : null}
                </section>
              ) : (
                statusMessage
              )}

              {returnHold ? (
                <div className="sa-alert" role="status">
                  <p>{t('common.returnHeld')}</p>
                  <Button
                    type="button"
                    disabled={retrying}
                    onClick={() => void payBooking(returnHold._id)}
                  >
                    {retrying
                      ? t('common.loading')
                      : t('common.payReturnAmount', {
                          amount: formatMoney(returnHold.totalAmount, returnHold.currency, isFa),
                        })}
                  </Button>
                </div>
              ) : null}

              <section className="booking-panel" aria-labelledby="ticket-pax-title">
                <h2 id="ticket-pax-title" className="booking-panel__title">
                  {t('common.passengers')}
                </h2>
                <div className="booking-table-wrap">
                  <table className="sa-table">
                    <thead>
                      <tr>
                        <th>{t('common.fullName')}</th>
                        <th>{t('common.document')}</th>
                        {confirmed ? <th>{t('common.ticketNumber')}</th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {booking.passengers.map((p, i) => (
                        <tr key={i}>
                          <td dir="ltr" style={{ textAlign: 'start' }}>
                            {p.firstName} {p.lastName}
                          </td>
                          <td dir="ltr" style={{ textAlign: 'start' }}>
                            {p.documentNumber}
                          </td>
                          {confirmed ? (
                            <td dir="ltr" style={{ textAlign: 'start' }}>
                              {p.ticketNumber ?? booking.ticketNumbers[i] ?? '—'}
                            </td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="booking-panel__total">
                  <span>{confirmed ? t('common.amountPaid') : t('common.totalPayable')}</span>
                  <strong>{formatMoney(booking.totalAmount, booking.currency, isFa)}</strong>
                </div>
              </section>

              <div className="booking-actions no-print">
                <Link to="/" className="sa-button sa-button--ghost">
                  {t('common.newSearch')}
                </Link>
                {confirmed && !returnHold && pendingFlights.length > 0 ? (
                  <Button
                    type="button"
                    onClick={() => {
                      const [next, ...rest] = pendingFlights;
                      if (rest.length) sessionStorage.setItem('sa_pending_flights', JSON.stringify(rest));
                      else sessionStorage.removeItem('sa_pending_flights');
                      navigate('/book', {
                        state: { flight: next, passengers: booking.passengers.length },
                      });
                    }}
                  >
                    {t('common.nextFlight')}
                  </Button>
                ) : null}
                {confirmed && !returnHold ? (
                  <Button type="button" onClick={() => window.print()}>
                    {t('common.printTicket')}
                  </Button>
                ) : holdActive ? (
                  <Button type="button" disabled={retrying} onClick={() => void payBooking(booking._id)}>
                    {retrying ? t('common.loading') : t('common.retryPayment')}
                  </Button>
                ) : null}
              </div>
            </div>

            <aside className="booking-layout__aside">
              {[booking, companion]
                .filter((item): item is BookingDetails => Boolean(item))
                .sort((a, b) => (legRole(a) === 'return' ? 1 : 0) - (legRole(b) === 'return' ? 1 : 0))
                .map((item) => (
                  <FlightSummaryCard
                    key={item._id}
                    title={
                      legRole(item)
                        ? t(legRole(item) === 'return' ? 'common.returnFlight' : 'common.outboundFlight')
                        : undefined
                    }
                    flight={{
                      flightNumber: item.flightId.flightNumber,
                      origin: item.flightId.origin,
                      destination: item.flightId.destination,
                      departureAt: item.flightId.departureAt,
                      cabin: item.cabin,
                    }}
                  />
                ))}
            </aside>
          </div>
        ) : null}
      </div>
    </main>
  );
}
